import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Divider,
  Alert,
  MenuItem,
  Grow,
  CircularProgress,
  useTheme,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import {
  fetchJsonDynamic,
  executeAnyMutation,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import { formatCurrency, parseErrorMessage } from "../stageBoardShared";
import { loanManagementUrl } from "../../../config";

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

// While typing, only group the integer part with commas — don't force a
// decimal point/trailing zeros on the user, that's what breaks typing "1500.5".
// Once the field is blurred, normalize to a clean 2-decimal amount.
const formatMoneyInput = (raw, focused) => {
  if (raw === "") return "";
  if (focused) {
    const [intPart, decPart] = raw.split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
  }
  return Number(raw).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const SettleRepossessedAssetDialog = ({ open, row, onClose, onSuccess, base_url }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [repossessionCost, setRepossessionCost] = useState("");
  const [assetSoldAmount, setAssetSoldAmount] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [repossessionCostFocused, setRepossessionCostFocused] = useState(false);
  const [assetSoldAmountFocused, setAssetSoldAmountFocused] = useState(false);

  const [customerLoans, setCustomerLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState("");

  // Load the customer's disbursed loans — the mutation needs a loanId to know
  // which loan this settlement is being recorded against.
  useEffect(() => {
    if (!open || !row?.CustomerId) {
      setCustomerLoans([]);
      setSelectedLoanId("");
      return;
    }
    (async () => {
      setLoansLoading(true);
      setSelectedLoanId("");
      const response = await fetchJsonDynamic(
        "loanApplicationByCustomer",
        "loans",
        { customerId: Number(row.CustomerId), isLoanCleared: 0 },
        loanManagementUrl.uri,
      );
      let rows = [];
      if (response && !response.error && response.results) {
        try {
          const parsed = JSON.parse(response.results);
          rows = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          rows = [];
        }
      }
      const eligible = rows.filter((loan) => loan.LoanDisbursed === 1);
      setCustomerLoans(eligible);
      if (eligible.length === 1) {
        setSelectedLoanId(eligible[0].Id);
      }
      setLoansLoading(false);
    })();
  }, [open, row?.CustomerId]);

  const resetAndClose = () => {
    if (submitting) return;
    setRepossessionCost("");
    setAssetSoldAmount("");
    setRepossessionCostFocused(false);
    setAssetSoldAmountFocused(false);
    setCustomerLoans([]);
    setSelectedLoanId("");
    setTouched(false);
    setErrorMessage(null);
    onClose();
  };

  const hasValidationError =
    repossessionCost === "" ||
    assetSoldAmount === "" ||
    Number(repossessionCost) < 0 ||
    Number(assetSoldAmount) < 0 ||
    !selectedLoanId;

  const handleSubmit = async () => {
    setTouched(true);
    setErrorMessage(null);
    if (hasValidationError) return;

    setSubmitting(true);
    try {
      const payload = {
        p_AssetId: row.Id,
        p_LoanId: Number(selectedLoanId),
        p_RepossessionCost: parseFloat(repossessionCost),
        p_AssetSoldAmount: parseFloat(assetSoldAmount),
      };

      const response = await executeAnyMutation(
        "settleRepossessedAsset",
        payload,
        "settleRepossessedAsset",
        false,
        base_url,
      );

      if (response?.error) {
        setErrorMessage(parseErrorMessage(response.error));
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Asset Settled",
        text: `${row.CustomerName || "The customer"}'s ${row.AssetTypeName || "asset"} has been settled and moved to Auctioned Assets.`,
      });
      onSuccess();
      resetAndClose();
    } catch (error) {
      setErrorMessage("An error occurred while settling the asset. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={GrowTransition}
      keepMounted={false}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(135deg, #1766A2 0%, #2196F3 100%)",
          color: "#fff",
        }}
      >
        <LocalOfferIcon />
        Settle Repossessed Asset
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={0.4} mb={1.5}>
          <Typography variant="caption" color={colors.grey[400]}>
            Customer
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {row.CustomerName || "—"}
            {row.CustomerPhone ? `  •  ${row.CustomerPhone}` : ""}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          {row.AssetTypeName && (
            <Chip label={row.AssetTypeName} size="small" color="primary" variant="outlined" />
          )}
          {row.SubTypeName && <Chip label={row.SubTypeName} size="small" variant="outlined" />}
          <Chip
            label={`Indicative: ${formatCurrency(row.IndicativeValue)}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Forced Sale Value: ${formatCurrency(row.CurrentFSV)}`}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ mb: 2, borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined }} />

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          select
          fullWidth
          label="Select Loan"
          value={selectedLoanId}
          onChange={(e) => setSelectedLoanId(e.target.value)}
          error={touched && !selectedLoanId}
          helperText={
            touched && !selectedLoanId
              ? "Select the loan this settlement is being recorded against"
              : loansLoading
                ? "Checking disbursed loans…"
                : " "
          }
          disabled={loansLoading || customerLoans.length === 0}
          sx={{ mb: 2 }}
        >
          {customerLoans.map((loan) => (
            <MenuItem key={loan.Id} value={loan.Id}>
              Loan #{loan.Id}
            </MenuItem>
          ))}
        </TextField>

        {!loansLoading && customerLoans.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No disbursed loans found for this customer.
          </Alert>
        )}

        <TextField
          label="Repossession Cost (KES)"
          type="text"
          inputMode="decimal"
          fullWidth
          required
          autoFocus
          value={formatMoneyInput(repossessionCost, repossessionCostFocused)}
          onChange={(e) => {
            const raw = e.target.value.replace(/,/g, "");
            if (/^\d*\.?\d*$/.test(raw)) setRepossessionCost(raw);
          }}
          onFocus={() => setRepossessionCostFocused(true)}
          onBlur={() => setRepossessionCostFocused(false)}
          error={touched && (repossessionCost === "" || Number(repossessionCost) < 0)}
          helperText={
            touched && (repossessionCost === "" || Number(repossessionCost) < 0)
              ? "Enter a valid repossession cost"
              : "Cost incurred to repossess this asset (recovery agent fees, towing, storage, etc.)"
          }
          sx={{ mb: 2 }}
        />

        <TextField
          label="Asset Sold Amount (KES)"
          type="text"
          inputMode="decimal"
          fullWidth
          required
          value={formatMoneyInput(assetSoldAmount, assetSoldAmountFocused)}
          onChange={(e) => {
            const raw = e.target.value.replace(/,/g, "");
            if (/^\d*\.?\d*$/.test(raw)) setAssetSoldAmount(raw);
          }}
          onFocus={() => setAssetSoldAmountFocused(true)}
          onBlur={() => setAssetSoldAmountFocused(false)}
          error={touched && (assetSoldAmount === "" || Number(assetSoldAmount) < 0)}
          helperText={
            touched && (assetSoldAmount === "" || Number(assetSoldAmount) < 0)
              ? "Enter a valid sold amount"
              : "Amount the asset was auctioned/sold for"
          }
        />
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={resetAndClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <LocalOfferIcon />
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            background: "linear-gradient(135deg, #1766A2 0%, #2196F3 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #145d92 0%, #1e88e5 100%)",
              boxShadow: "0 4px 12px rgba(23,102,162,0.4)",
            },
            transition: "all 0.18s ease",
          }}
        >
          {submitting ? "Settling..." : "Settle Asset"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettleRepossessedAssetDialog;
